import { ReactElement, useCallback, useRef, useState } from 'react';
import { DrawerBody, DrawerHeader, Flex, Box, Text, Drawer } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { EditorControl } from '../common/components/lexical';
import { LoadingIndicator } from '../common/components/loading-indicator';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { Conversations } from '../messaging/components/conversations-subpanel';
import { useMessagingStore } from '../messaging/hooks/use-messaging-store';
import { useNewConversationCancellationModal } from '../messaging/hooks/use-new-conversation-cancellation-modal';
import { MessageLoadFailBanner } from '../messaging/components/message-load-fail-banner';
import { ConversationsFilterBar } from '../messaging/components/conversations-filter-bar/filter-bar';
import { NewConversation } from '../messaging/components/new-conversation-subpanel';
import { Messages } from '../messaging/components/messages-subpanel';
import { NewConversationCancellationModalProvider } from '../messaging/context/new-conversation-cancellation-modal-context';
import { MessagingBookmarksButton } from '../messaging/components/messaging-bookmarks-button';

const FrMessagingDrawer = observer(function FrMessagingDrawer(): ReactElement {
    const [showAllConversations, setShowAllConversations] = useState(true);

    const drawerControls = useDrawerControls();
    const {
        isCreatingNewConversation,
        messagingBootstrapState,
        isBookmarksView,
    } = useMessagingStore();
    const defaultFocusElementRef = useRef<HTMLDivElement | null>(null);
    const editorControlRef = useRef<EditorControl>();

    const closeDrawer = useCallback(() => {
        drawerControls.close();
    }, [drawerControls]);

    const { NewConversationCancellationModal } = useNewConversationCancellationModal(
        setShowAllConversations
    );

    return (
        <>
            <Drawer isOpen size="lg" onClose={closeDrawer}>
                <DrawerHeader>
                    <Flex width="92%" justifyContent="space-between" alignItems="center">
                        <Text variant="headingLg" style={{ height: '40px' }}>
                            Messages
                        </Text>
                        <MessagingBookmarksButton
                            setShowAllConversations={setShowAllConversations}
                        />
                    </Flex>
                </DrawerHeader>
                <DrawerBody sx={{ padding: 0 }}>
                    <Flex flexDirection="column" w="100%" h="100%">
                        <MessageLoadFailBanner
                            testId="messaging-bootstrap-load-retry"
                            isError={messagingBootstrapState?.isError}
                            refetch={messagingBootstrapState?.refetch}
                        />
                        {messagingBootstrapState?.isFetching ? (
                            <LoadingIndicator />
                        ) : (
                            <>
                                <Flex>
                                    <Box w="100%">
                                        <ConversationsFilterBar
                                            defaultFocusElementRef={defaultFocusElementRef}
                                            setShowAllConversations={setShowAllConversations}
                                        />
                                    </Box>
                                </Flex>
                                {showAllConversations && (
                                    <Conversations
                                        setShowAllConversations={setShowAllConversations}
                                    />
                                )}
                                {!showAllConversations && (
                                    <Flex
                                        data-testid="messaging-conversation-history-view"
                                        h="100%"
                                        bg="var(--arc-colors-surface-background)"
                                        flexDirection="column"
                                    >
                                        {isCreatingNewConversation && (
                                            <NewConversation
                                                isBookmarksView={isBookmarksView}
                                                editorControlRef={editorControlRef}
                                                setShowAllConversations={setShowAllConversations}
                                            />
                                        )}
                                        {!isCreatingNewConversation && (
                                            <Messages
                                                editorControlRef={editorControlRef}
                                                setShowAllConversations={setShowAllConversations}
                                            />
                                        )}
                                    </Flex>
                                )}
                            </>
                        )}
                    </Flex>
                </DrawerBody>
            </Drawer>
            <NewConversationCancellationModal />
        </>
    );
});

export function WrappedFrMessagingDrawer(): ReactElement {
    return (
        <NewConversationCancellationModalProvider>
            <FrMessagingDrawer />
        </NewConversationCancellationModalProvider>
    );
}

export type FrMessagingDrawerType = typeof FrMessagingDrawer;
